import React, {useCallback, useState} from 'react';
import {Typography} from '@mui/material';
import type {RowSelectionModel} from 'components/dataTable/types';
import type {NorafUpdate} from 'services/agent/types';
import {useLocalization} from 'localization';
import {
  useDeleteNorafUpdateMutation,
  usePostNorafUpdatesIgnorelistMutation,
} from 'services/agent';
import {
  CheckboxField,
  ControlledIconButton,
  FlexBox,
  useSnacks,
} from 'components';
import {settlePromiseList} from '../functions/settlePromiseList';

type Props = {
  selection: RowSelectionModel;
  items: NorafUpdate[];
  postProcessing: () => void;
  postSuccess: () => void;
};
export const NorafChangesSelectionActions: React.FC<Props> = ({
  selection,
  items,
  postProcessing,
  postSuccess,
}) => {
  const {t} = useLocalization();
  const {successSnack, errorSnack} = useSnacks();

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [ignoreFutureChanges, setIgnoreFutureChanges] =
    useState<boolean>(false);

  const [deleteNorafUpdate] = useDeleteNorafUpdateMutation();
  const [postIgnoreItem] = usePostNorafUpdatesIgnorelistMutation();

  const handleDeleteSelected = useCallback(() => {
    setIsProcessing(true);

    const deletePromises: Promise<void>[] = [];
    selection.ids.forEach(id => {
      deletePromises.push(deleteNorafUpdate(id as string).unwrap());
    });

    const ignorePromises: Promise<void>[] = [];

    if (ignoreFutureChanges) {
      selection.ids.forEach(id => {
        const item = items.find(i => i.id === id);
        if (item) {
          ignorePromises.push(postIgnoreItem(item).unwrap());
        }
      });
    }

    const deletions = settlePromiseList(deletePromises);
    const ignores = settlePromiseList(ignorePromises);

    deletions.then(deleteResponse => {
      if (deleteResponse.rejected > 0) {
        errorSnack(
          t(`page.agents.noraf.changes.delete.failed`, {
            count: deleteResponse.rejected,
          }),
        );
      } else {
        successSnack(
          t(`page.agents.noraf.changes.delete.success`, {
            count: deleteResponse.fulfilled,
          }),
        );

        ignores.then(ignoredResponse => {
          if (ignoredResponse.rejected > 0) {
            errorSnack(
              t(`page.agents.noraf.changes.ignore.failed`, {
                count: ignoredResponse.rejected,
              }),
            );
          } else if (ignoredResponse.fulfilled > 0) {
            successSnack(
              t(`page.agents.noraf.changes.ignore.success`, {
                count: ignoredResponse.fulfilled,
              }),
            );
          }
        });

        postSuccess();
      }

      postProcessing();
      setIsProcessing(false);
    });
  }, [
    items,
    deleteNorafUpdate,
    errorSnack,
    ignoreFutureChanges,
    postIgnoreItem,
    postSuccess,
    postProcessing,
    selection,
    successSnack,
    t,
  ]);

  if (!selection.ids.size) {
    return null;
  }

  return (
    <FlexBox left center>
      <Typography variant="body1">
        {t(`page.agents.noraf.changes.selection`, {
          count: selection.ids.size,
        })}
      </Typography>
      <CheckboxField
        boxLabel={t('page.agents.noraf.changes.ignore.title')}
        fieldId={'ignoreFutureChanges'}
        name={'ignoreFutureChanges'}
        value={ignoreFutureChanges}
        onChange={setIgnoreFutureChanges}
      />
      <ControlledIconButton
        icon={'Delete'}
        postText={
          <Typography variant={'body1'}>
            {t('page.agents.noraf.changes.delete.title')}
          </Typography>
        }
        onClick={handleDeleteSelected}
        isCalling={isProcessing}
        isDelayed={isProcessing}
        size={'small'}
      />
    </FlexBox>
  );
};
